import { useEffect, useCallback, useState } from "react";

interface KeyboardShortcutsConfig {
  onPlayPause: () => void;
  onNext: () => void;
  onPrev: () => void;
  onSearch: () => void;
  onShowHotkeys: () => void;
  isEnabled?: boolean;
}

/**
 * Hook for global keyboard shortcuts.
 * - Space: Play/Pause
 * - ArrowRight: Next station
 * - ArrowLeft: Previous station
 * - /: Focus search
 * - Escape: Close modals (handled by components)
 * - ?: Show hotkey menu
 */
export function useKeyboardShortcuts({
  onPlayPause,
  onNext,
  onPrev,
  onSearch,
  onShowHotkeys,
  isEnabled = true,
}: KeyboardShortcutsConfig) {
  const [isHotkeyMenuOpen, setIsHotkeyMenuOpen] = useState(false);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isEnabled) return;

      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      // Allow Escape to always work
      if (event.key === "Escape") {
        if (isHotkeyMenuOpen) {
          setIsHotkeyMenuOpen(false);
          event.preventDefault();
        }
        return;
      }

      // Don't handle other shortcuts when typing
      if (isTyping) return;

      switch (event.key) {
        case " ":
          event.preventDefault();
          onPlayPause();
          break;
        case "ArrowRight":
          event.preventDefault();
          onNext();
          break;
        case "ArrowLeft":
          event.preventDefault();
          onPrev();
          break;
        case "/":
          event.preventDefault();
          onSearch();
          break;
        case "?":
          event.preventDefault();
          setIsHotkeyMenuOpen(true);
          onShowHotkeys();
          break;
      }
    },
    [
      isEnabled,
      onPlayPause,
      onNext,
      onPrev,
      onSearch,
      onShowHotkeys,
      isHotkeyMenuOpen,
    ],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return {
    isHotkeyMenuOpen,
    setIsHotkeyMenuOpen,
    closeHotkeyMenu: () => setIsHotkeyMenuOpen(false),
  };
}

// Shortcut definitions for display in menu
export const KEYBOARD_SHORTCUTS = [
  { key: "Space", description: "Play / Pause" },
  { key: "←", description: "Previous station" },
  { key: "→", description: "Next station" },
  { key: "/", description: "Focus search" },
  { key: "Esc", description: "Close menus" },
  { key: "?", description: "Show shortcuts" },
];
